import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap'; // ✅ Spinner for loader
import Input from 'shared/form/Input/Input';
import style from './Tasks.module.css';
import Task from './Task';
import plusIcon from 'assets/img/plus.svg';
import DueDateSelection from './DueDateSelection';
import AssignSelection from './AssignSelection';
import Select from 'react-select';
import { hasOnlySpecialCharacters } from '../../utils/general';
import DeleteModal from 'components/DeleteModal';
import { getPlainText, hasLink, titleLimit } from 'utils/Helpers';
import APIServices from 'services/APIServices';
import ReactQuill from 'react-quill';
import ConfirmModal from 'components/ConfirmModal';
import boardToast from '../../utils/boardToast';
import { TOAST_TYPE } from 'utils/AppToast';
import { useAuth } from 'contexts/AuthContext';
import CustomDatepicker from '../../shared/CustomDatepicker/CustomDatepicker';

export default function Tasks({
  tasks,
  handleAddTask,
  handleChecked,
  viewMode,
  members,
  statusOptions,
  isSubTask,
  handleDelete,
  fetchSubtask,
  isDeleting,
  reminderOptions
}) {
  const reactQuillRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState(null);
  const [error, setError] = useState('');
  const [editableTaskId, setEditableTaskId] = useState(null);
  const [editedTask, setEditedTask] = useState(null); // holds temp changes
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const addItem = () => {
    setNewItem({
      name: '',
      dueDate: null,
      status: statusOptions[0]?.value, // default to "to-do"
      assignees: [],
      originalUrl: '' // ✅ store link separately
    });
    setEditableTaskId(null);
    setEditedTask(null);
  };

  useEffect(() => {
    handleCancel();
  }, [isSubTask]);

  const handleCancel = () => {
    setNewItem(null);
    setError('');
    setEditableTaskId(null);
    setEditedTask(null);
  };

  const handleAddTasks = () => {
    const plainText = newItem.name.replace(/<[^>]+>/g, '').trim();

    if (!plainText) {
      setError('Please enter value');
      return;
    } else if (plainText.length < 2) {
      setError('Min 2 character required');
      return;
    } else if (hasOnlySpecialCharacters(plainText)) {
      setError('The input contains only special characters. Please enter valid text.');
      return;
    } else {
      handleAddTask(newItem);
      setNewItem();
      setError('');
    }
  };

  const handleDeleteItem = () => {
    setLoading(true);
    handleDelete(itemToDelete);
    setItemToDelete(null);
    setLoading(false);
    setShowConfirm(false);
  };

  const handleDueDate = (date, kbDueDateReminders = []) => {
    let dueDateString = '';
    const validDate = date === null ? null : date instanceof Date ? date : new Date(date);
    if (validDate) {
      dueDateString = validDate.toLocaleString('en-US', {
        month: 'short', // "Nov"
        day: 'numeric', // "20"
        hour: 'numeric', // "8"
        minute: '2-digit', // "00"
        hour12: true // "PM"
      });
    }
    // if (kbDueDateReminderSet) {
    setNewItem({ ...newItem, assignees: [...newItem.assignees, user._id] });
    // }
    setNewItem({
      ...newItem,
      dueDate: date instanceof Date ? date : new Date(date),
      dueDateString,
      kbDueDateReminders
    });
  };

  const handleStatusChange = (status) => {
    setNewItem({ ...newItem, status: status.value });
  };

  const handleMember = (data) => {
    if (newItem.assignees?.length > 0) {
      if (newItem.assignees.find((value) => value._id !== data._id)) {
        setNewItem({ ...newItem, assignees: [...newItem.assignees, data] });
      }
    } else {
      setNewItem({ ...newItem, assignees: [data] });
    }
  };

  const handleRemove = (userId) => {
    const filterTeam = newItem.assignees?.filter((value) => value._id !== userId);
    setNewItem({ ...newItem, assignees: filterTeam });
  };

  const handleLinkPreview = async (url, index) => {
    try {
      setIsLoading(true);
      const response = await APIServices.post('/link/preview', { url });

      if (response?.title) {
        const quill = reactQuillRef.current.getEditor();

        // Remove the raw URL
        quill.deleteText(index, url.length);

        // Insert the fetched title as a hyperlink
        quill.insertText(index, response.title, { link: url });
      }
    } catch (err) {
      console.error('Error fetching link preview:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextChange = (content, delta, source, editor) => {
    const plainText = editor.getText().trim();
    // if (!plainText) {
    //   setError('Please enter value');
    //   return;
    // } else if (plainText.length < 2) {
    //   setError('Min 2 character required');
    //   return;
    // } else if (hasOnlySpecialCharacters(plainText)) {
    //   setError('The input contains only special characters. Please enter valid text.');
    //   return;
    // } else {
    // ✅ enforce char limit of 20
    if (plainText.length > titleLimit) {
      const truncated = plainText.substring(0, titleLimit);

      // force truncate content in editor
      const quill = reactQuillRef.current.getEditor();
      quill.deleteText(0, quill.getLength());
      quill.insertText(0, truncated);

      // handleTitleChange(truncated);
      return;
    }
    setNewItem((prev) => ({ ...prev, name: content }));

    if (source === 'user') {
      const matches = [...plainText.matchAll(urlRegex)];
      if (matches.length > 0) {
        const lastMatch = matches[matches.length - 1];
        const url = lastMatch[0];
        const index = lastMatch.index;
        handleLinkPreview(url, index);
      }
      // }
    }
  };

  const confirmContent = (
    <>
      <h2 className="mb-3">Are you sure?</h2>
      <h3>You want to delete this {isSubTask ? 'subtask' : 'checklist'}?</h3>
    </>
  );

  const handleSaveTask = async () => {
    console.log(editedTask.name);

    const hasAnchorLink = hasLink(editedTask.name);

    let finalTitle;

    if (hasAnchorLink) {
      console.log('Contains link, sending full HTML');
      finalTitle = editedTask.name; // send full data
    } else {
      console.log('No link, sending plain text');
      finalTitle = getPlainText(editedTask.name); // send plain text
    }

    if (!finalTitle) {
      boardToast('Please enter title', TOAST_TYPE.ERROR);
      return;
    } else if (finalTitle.length < 2) {
      boardToast('Min 2 character required for title', TOAST_TYPE.ERROR);
      return;
    } else if (hasOnlySpecialCharacters(finalTitle)) {
      boardToast('The input contains only special characters. Please enter valid text.', TOAST_TYPE.ERROR);
      return;
    } else {
      handleChecked({ ...editedTask, name: finalTitle }, 'all');
      setEditableTaskId(null);
      setEditedTask(null);
    }
  };

  // Inside your Tasks component
  useEffect(() => {
    if (newItem && reactQuillRef.current) {
      const quill = reactQuillRef.current.getEditor();
      quill.focus();
    }
  }, [newItem]);

  // Regex to detect URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return (
    <div className={style.checklist_wrapper}>
      <div className={style.tasks_wrapper}>
        <div className={style.subtask_heading_cover}>
          <div className={style.heading_user_name}>Name</div>
          <div className={style.heading_user_details}>
            <div className={style.heading_assignee}>Assignee</div>
            {isSubTask && <div className={style.heading_status}>Status</div>}
            <div className={style.heading_due_date}>Due date</div>
            <div></div>
          </div>
        </div>
        <div className={style.task_content_cover}>
          {tasks?.map((value) => (
            <>
              {/* {console.log(value, 'aaaaaaaaaaaaaaaaaaaaaaaaaa')} */}

              <Task
                key={value._id}
                reminderOptions={reminderOptions}
                milestone={value}
                isCompleted={value.isCompleted}
                dropdownOpen={openDropdownId === value?._id}
                onDropdownToggle={(isOpen) => setOpenDropdownId(isOpen ? value._id : null)}
                // ✅ If originalUrl exists, render clickable link
                title={editableTaskId === value._id && editedTask ? editedTask?.name : value.name}
                dueDate={
                  (editableTaskId === value._id && editedTask ? editedTask?.dueDate : value.dueDate)
                    ? new Date(editableTaskId === value._id && editedTask ? editedTask?.dueDate : value.dueDate)
                    : null
                }
                assignees={editableTaskId === value._id && editedTask ? editedTask?.assignees : value.assignees}
                handleChecked={(e) => {
                  setEditedTask((prev) => ({ ...prev, isCompleted: e.target.checked }));
                  handleChecked({ ...value, isCompleted: e.target.checked }, 'isCompleted');
                }}
                disabled={viewMode}
                statusOptions={statusOptions}
                status={
                  editableTaskId === value._id && editedTask
                    ? editedTask?.status?._id || editedTask?.status
                    : value?.status?._id
                      ? value?.status?._id
                      : value?.status
                }
                isSubTask={isSubTask}
                isEditable={editableTaskId === value._id}
                handleStatusChange={(status) => {
                  setEditedTask((prev) => ({ ...prev, status }));
                  handleChecked({ ...value, status }, 'status');
                }}
                members={members}
                handleDueDateChange={(date, kbDueDateReminders = []) => {
                  // console.log(kbDueDateReminderSet, kbDueDateReminderId);

                  let dueDateString = '';
                  const validDate = date === null ? null : date instanceof Date ? date : new Date(date);
                  if (validDate) {
                    dueDateString = validDate.toLocaleString('en-US', {
                      month: 'short', // "Nov"
                      day: 'numeric', // "20"
                      hour: 'numeric', // "8"
                      minute: '2-digit', // "00"
                      hour12: true // "PM"
                    });
                  }
                  const prevIds = (value?.assignees || []).map((a) => (typeof a === 'string' ? a : a._id));

                  setEditedTask((prev) => ({
                    ...prev,
                    dueDate: validDate,
                    dueDateString,
                    kbDueDateReminders
                  }));
                  handleChecked({ ...value, dueDate: validDate, dueDateString, kbDueDateReminders }, 'dueDate');
                  if (!prevIds.includes(user._id) && kbDueDateReminders?.[0]?.kbDueDateReminderSet) {
                    setEditedTask((prev) => ({
                      ...prev,
                      assignees: [...prevIds, user._id]
                    }));
                    handleChecked({ ...value, assignees: [...prevIds, user._id] }, 'assignees');
                  }
                }}
                fetchSubtask={() => fetchSubtask(value._id)}
                handleTitleChange={(newTitle) => {
                  setEditedTask((prev) => ({
                    ...prev,
                    name: newTitle
                  }));
                }}
                handleAddAssignee={(memberId) => {
                  const prevIds = (value?.assignees || []).map((a) => (typeof a === 'string' ? a : a._id));
                  if (prevIds.includes(memberId)) return value; // already added
                  setEditedTask((prev) => ({
                    ...prev,
                    assignees: [...prevIds, memberId]
                  }));
                  handleChecked({ ...value, assignees: [...prevIds, memberId] }, 'assignees');
                }}
                handleRemoveAssignee={(memberId) => {
                  const prevIds = value?.assignees || [];
                  const remainingIds = prevIds.filter((x) => {
                    if (typeof x === 'string') return x !== memberId;
                    if (typeof x === 'object' && x !== null) return x._id !== memberId;
                    return true; // Keep anything unexpected just in case
                  });
                  setEditedTask((prev) => ({
                    ...prev,
                    assignees: remainingIds
                  }));
                  handleChecked({ ...value, assignees: remainingIds }, 'assignees');
                }}
                handleDisableTask={() => {
                  setEditableTaskId(null);
                  setEditedTask(null);
                }}
                handleSetEditableTask={() => {
                  setEditableTaskId(value._id);
                  setEditedTask(value);
                  // handleChecked(value);
                }}
                handleSaveTask={() => {
                  handleSaveTask();
                }}
                cancelNewItem={() => {
                  setNewItem(null);
                }}
                handleDeleteTask={() => {
                  setItemToDelete(value);
                  setShowConfirm(true);
                }}
              />
              {/* {!isSubTask && ( */}
              <div className={'w-100'}>
                <div className="editdelete-text">
                  <>
                    {editableTaskId === value._id && (
                      <div className="d-flex  gap-2">
                        <Button
                          variant="success"
                          onClick={() => {
                            handleSaveTask();
                          }}>
                          Save
                        </Button>
                        <Button variant="link" onClick={() => setEditableTaskId(null)}>
                          Cancel
                        </Button>
                      </div>
                    )}{' '}
                  </>
                </div>
              </div>
              {/* )} */}
            </>
          ))}
        </div>
        {!viewMode && (
          <>
            {!newItem && (
              <Button variant="light" className={style.add_item_button} onClick={addItem}>
                <div className="icon-btn">
                  <img src={plusIcon} alt="search-icon" className="search-icon-right" />
                </div>
                {isSubTask ? 'Add subtask' : 'New checklist item'}
              </Button>
            )}
            {newItem && (
              <div className="w-100">
                <>
                  <ReactQuill
                    className={style.quill_input}
                    title={newItem.name}
                    ref={reactQuillRef}
                    value={newItem.name || ''}
                    onChange={handleTextChange}
                    placeholder="Add an item"
                    modules={{ toolbar: false }} // 🚫 No toolbar
                    // formats={[]} // Disables all formatting
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTasks();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        handleCancel();
                      }
                    }}
                  />

                  {isLoading && (
                    <div className="mt-2 d-flex align-items-center text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Fetching title…
                    </div>
                  )}
                </>
                {/* )} */}

                <div className="mt-2 d-flex justify-content-between add_subtask">
                  <div className="d-flex gap-2">
                    <Button variant="success" onClick={handleAddTasks}>
                      Save
                    </Button>
                    <Button variant="link" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </div>

                  <div className="d-flex gap-2 add_subtask_asign">
                    {isSubTask && (
                      <Select
                        className="select-todo"
                        options={statusOptions}
                        value={statusOptions.find((option) => option.value === newItem?.status)}
                        onChange={handleStatusChange}
                        menuPlacement="auto"
                        menuPosition="absolute"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                        menuPortalTarget={document.body}
                      />
                    )}
                    {/* <DueDateSelection showTimeSelect={true} handleDueDate={handleDueDate} dueDate={newItem?.dueDate} /> */}

                    <CustomDatepicker
                      defaultDate={newItem?.dueDate}
                      handleDueDate={handleDueDate}
                      reminderOptionsData={reminderOptions}
                      // milestone={milestone}
                      showDateOnly={true}
                    />
                    <div className="milestone-task-checklist">
                      <AssignSelection
                        assignees={newItem?.assignees}
                        members={members}
                        handleMember={handleMember}
                        handleRemove={handleRemove}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <ConfirmModal
          show={showConfirm}
          onHide={() => {
            setShowConfirm(false);
            setItemToDelete(null);
          }}
          confirmationText={confirmContent}
          handleAction={handleDeleteItem}
          closeText="Yes"
          loading={isDeleting}
          bgClassname={'whitebgmodal'}
        />
      </div>
    </div>
  );
}
